import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Checkbox, Input, Space, Text, toast } from '@conexasaude/conexa-hero'
import Modal from '@/components/Modal'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import services from '@/modules/Auth/auth-services'
import useStyles from './styles'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useForm } from 'react-hook-form'
import { TermsAndConditionsForm } from './types'
import { zodResolver } from '@hookform/resolvers/zod'
import { termsAndConditionsFormSchema } from './validations'
import { RenderIf } from '@/components/RenderIf'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { getDigitsFromString } from '@/utils/getDigitsFromString'
import { sha256Hash } from '@conexasaude/analytics'
import { User } from '../../classes/User'

interface TermsAndConditionsModalProps {
  redirectTo: string
  directLogin: () => Promise<void>
  setLoading: (isLoading: boolean) => void
  userToken: string
}

const TermsAndConditionsModal: FunctionComponent<
  TermsAndConditionsModalProps
> = ({ redirectTo, directLogin, setLoading, userToken }) => {
  const { closeModal, showModal } = useModalContext()
  const { embed } = useThemeGeneratorContext()
  const { users } = useAuthContext()
  const { logAnalyticsEvent } = useFirebaseContext()

  const userSelected = useMemo(() => {
    const user = users?.find(user => user.token === userToken)

    /*
     it's necessary to clear the mail field as users registered via api are
     being registered with this domain (sememail.com)
    */
    if (user?.email?.includes('@sememail.com')) {
      user.email = ''
    }

    return user
  }, [userToken, users])

  const sendAnalyticsEventTermsAndConditionsModal = useCallback(
    (user: User) => {
      if (!user) return

      const normalizedCPF = getDigitsFromString(user?.cpf)
      const hashedCPF = sha256Hash(normalizedCPF)

      logAnalyticsEvent('at_patient_accept_terms', {
        user_id: hashedCPF,
        cnx_user_id: hashedCPF,
        id_clinica: user?.clinic?.id,
        account_type: user?.clinic?.product,
        has_crp_concil:
          user?.clinicSpecialites?.psyAlive?.habilitado ||
          user?.clinicSpecialites?.psychology?.habilitado,
        patient_type: user?.holder,
        interaction: 'seen',
      })
    },
    [logAnalyticsEvent],
  )

  useEffect(() => {
    if (userSelected) sendAnalyticsEventTermsAndConditionsModal(userSelected)
  }, [userSelected, sendAnalyticsEventTermsAndConditionsModal])

  const getUserFilledFields = useMemo(() => {
    const fields = {
      cpf: !!userSelected?.cpf,
      email: !!userSelected?.email,
      birthDate: !!userSelected?.birthdate,
      phone: !!userSelected?.cellphone,
    }

    const filledFields: Partial<Record<keyof typeof fields, true>> =
      Object.fromEntries(
        Object.entries(fields)
          .filter(([, value]) => value)
          .map(([key]) => [key, true]),
      )

    const hasEmptyField = Object.values(fields).some(value => !value)

    return {
      hasEmptyField: hasEmptyField,
      filledFields,
    }
  }, [userSelected])

  const handleTermsAndConditionsFormSchema = () => {
    return termsAndConditionsFormSchema.omit(getUserFilledFields.filledFields)
  }

  const form = useForm<TermsAndConditionsForm>({
    resolver: zodResolver(handleTermsAndConditionsFormSchema()),
    values: {
      cpf: userSelected?.cpf || '',
      email: userSelected?.email || '',
      birthDate: userSelected?.birthdate || '',
      phone: userSelected?.cellphone || '',
      acceptTerms: false,
    },
  })

  const [termsUrl, setTermsUrl] = useState('')

  const styles = useStyles()

  const acceptTermsAndConditions = async (
    formValues: TermsAndConditionsForm,
  ) => {
    try {
      closeModal()
      setLoading(true)

      const userData = {
        cpf: formValues.cpf ?? userSelected?.cpf,
        birthDate: formValues.birthDate ?? userSelected?.birthdate,
        email: formValues.email ?? userSelected?.email,
        phone: formValues.phone ?? userSelected?.cellphone,
      }

      await services.acceptTerm(userData)

      const normalizedCPF = getDigitsFromString(userSelected?.cpf)
      const hashedCPF = sha256Hash(normalizedCPF)

      logAnalyticsEvent('at_patient_accept_terms', {
        user_id: hashedCPF,
        cnx_user_id: hashedCPF,
        id_clinica: userSelected?.clinic?.id,
        account_type: userSelected?.clinic?.product,
        has_crp_concil:
          userSelected?.clinicSpecialites?.psyAlive?.habilitado ||
          userSelected?.clinicSpecialites?.psychology?.habilitado,
        patient_type: userSelected?.holder,
        interaction: 'accept_terms_click',
      })

      await directLogin()

      setLoading(false)
      const createEmbedUrl = (url?: string) => {
        let parsedUrl: string

        if (embed.full) {
          parsedUrl = `/${url}?embedFull=true`
        } else if (embed.value) {
          parsedUrl = `/${url}?embed=true`
        } else {
          parsedUrl = `/${url}`
        }

        return parsedUrl
      }
      if (redirectTo) {
        window.location.pathname = createEmbedUrl(redirectTo)
      } else {
        window.location.href = createEmbedUrl('')
      }
    } catch (error) {
      setLoading(false)
      showModal('TERMS_AND_CONDITIONS')

      toast({
        message: error?.message,
        autoCloseDuration: 3000,
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    if (userToken) {
      setLoading(true)
      services
        .getClinic(userToken)
        .then(res => {
          const clinicTerms = res.clinic.urlTermoPaciente
          setTermsUrl(clinicTerms)
        })
        .catch(() => {
          setTermsUrl(
            'https://s3.sa-east-1.amazonaws.com/drvirtual/downloads/termo_uso_b2c.pdf',
          )
        })
        .finally(() => setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken])

  const goToTermsUrl = () => {
    window.open(termsUrl, '_blank')
  }

  const watchers = {
    acceptTerms: form.watch('acceptTerms'),
  }

  const shouldDisabledSubmitButton = useMemo(() => {
    return watchers.acceptTerms === false
  }, [watchers.acceptTerms])

  return (
    <Modal
      id="TERMS_AND_CONDITIONS"
      primaryActionLabel="Continuar"
      primaryAction={form.handleSubmit(acceptTermsAndConditions)}
      disabledPrimaryAction={shouldDisabledSubmitButton}
      size={480}
    >
      <div>
        <Space h="xs" />

        <Text variant="paragraphLarge">
          {getUserFilledFields?.hasEmptyField
            ? 'Para continuar, preencha os dados abaixo e aceite os '
            : 'Para continuar, pedimos que aceite os '}
          <Text
            variant="button"
            renderAs="span"
            css={styles.textLink}
            onClick={goToTermsUrl}
          >
            termos e condições gerais de uso
          </Text>
        </Text>

        <form onSubmit={form.handleSubmit(acceptTermsAndConditions)}>
          <RenderIf condition={!userSelected?.cpf}>
            <Space h="md" />
            <Input
              label="CPF*"
              placeholder="000.000.000-00"
              mask="999.999.999-99"
              fullWidth
              {...form.register('cpf')}
              error={form.formState.errors?.cpf?.message}
            />
          </RenderIf>

          <RenderIf condition={!userSelected?.birthdate}>
            <Space h="md" />
            <Input
              label="Data de Nascimento*"
              placeholder="DD/MM/AAAA"
              mask="99/99/9999"
              fullWidth
              {...form.register('birthDate')}
              error={form.formState.errors?.birthDate?.message}
            />
          </RenderIf>

          <RenderIf condition={!userSelected?.email}>
            <Space h="md" />
            <Input
              label="Email*"
              placeholder="Digite o seu email"
              fullWidth
              {...form.register('email')}
              error={form.formState.errors?.email?.message}
            />
          </RenderIf>

          <RenderIf condition={!userSelected?.cellphone}>
            <Space h="md" />
            <Input
              label="Telefone*"
              placeholder="(00) 00000-0000"
              mask="(99) 99999-9999"
              fullWidth
              {...form.register('phone')}
              error={form.formState.errors?.phone?.message}
            />
          </RenderIf>

          <Space h="md" />

          <Checkbox
            error={!!form.formState.errors?.acceptTerms?.message}
            label={
              <Text renderAs="p" variant="paragraphSmall">
                Li e aceito os{' '}
                <Text
                  variant="paragraphSmall"
                  renderAs="span"
                  css={styles.textLink}
                  onClick={goToTermsUrl}
                >
                  termos e condições gerais de uso
                </Text>
              </Text>
            }
            {...form.register('acceptTerms')}
          />

          <Space h="md" />
        </form>
      </div>
    </Modal>
  )
}

export { TermsAndConditionsModal }
